import { TrendingDownIcon, TrendingFlatIcon, TrendingUpIcon } from "@/components/appIcons";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { formatCountry } from "@garimpa/lib/countries";

export function providerDisplay(offer: DealOffer, locale: string) {
  const market = formatCountry(offer.country, locale, { includeUnknown: true }) || null;
  return {
    provider: offer.providerBrandKey,
    name: offer.providerDisplayName,
    market,
  };
}

export function offerMarketDisplay(offer: DealOffer, locale: string) {
  if (offer.shippingOriginCountry) return offer.shippingOriginCountry;
  return providerDisplay(offer, locale).market;
}

export function formatCommercePrice(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
  locale?: string,
): string | null {
  if (!amount || !currency) return null;
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(value)) return null;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatCompactCommercePrice(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
  locale?: string,
): string | null {
  if (!amount || !currency) return null;
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(value)) return null;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.trunc(value));
}

export function isPriceDropAtLeastThreshold(input: {
  currentPrice: string | number | null | undefined;
  referencePrice: string | number | null | undefined;
  threshold: number;
}): boolean {
  const currentPrice = Number(input.currentPrice);
  const referencePrice = Number(input.referencePrice);

  return (
    Number.isFinite(currentPrice) &&
    Number.isFinite(referencePrice) &&
    currentPrice > 0 &&
    referencePrice > currentPrice &&
    (referencePrice - currentPrice) / referencePrice >= input.threshold
  );
}

export function effectiveDealPrice(offer: Pick<DealOffer, "currentPriceTotal">) {
  return offer.currentPriceTotal;
}

export function isInternationalDealOffer(offer: Pick<DealOffer, "currentPriceInstructionKey">) {
  return offer.currentPriceInstructionKey?.includes(".international") ?? false;
}

export function dealOfferShippingCountsTowardTotal(
  offer: Pick<DealOffer, "currentShippingCountsTowardTotal">,
) {
  return offer.currentShippingCountsTowardTotal;
}

export function formatPriceDeltaPercent(value: string | null, locale?: string): string | null {
  if (!value) return null;
  const ratio = Number(value);
  if (!Number.isFinite(ratio) || ratio === 0) return null;
  if (Math.round(Math.abs(ratio) * 100) === 0) return null;

  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(Math.abs(ratio));
}

function humanizeOfferCode(value: string): string {
  const text = value.replace(/[_-]+/g, " ").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function formatCondition(
  condition: string | null,
  t: (key: "newCondition" | "usedCondition" | "refurbishedCondition") => string,
): string | null {
  if (!condition) return null;
  switch (condition.toLowerCase()) {
    case "new":
      return t("newCondition");
    case "used":
      return t("usedCondition");
    case "refurbished":
      return t("refurbishedCondition");
    default:
      return humanizeOfferCode(condition);
  }
}

export function dealMovementMeta(
  movement: string,
  t: (key: "priceDown" | "priceUp" | "priceStable") => string,
) {
  if (movement === "down") {
    return {
      icon: TrendingDownIcon,
      label: t("priceDown"),
      className: "text-emerald-400",
      chipClassName: "bg-emerald-400/10 text-emerald-400",
    };
  }

  if (movement === "up") {
    return {
      icon: TrendingUpIcon,
      label: t("priceUp"),
      className: "text-amber-300",
      chipClassName: "bg-amber-300/10 text-amber-300",
    };
  }

  if (movement === "flat") {
    return {
      icon: TrendingFlatIcon,
      label: t("priceStable"),
      className: "text-muted-foreground",
      chipClassName: "text-muted-foreground",
    };
  }

  return null;
}

function directionalMovementSignal(input: {
  deltaPercent?: string | null;
  locale: string;
  movement?: string | null;
  source: "deal" | "lastChange";
  t: (key: "priceDown" | "priceUp" | "priceStable" | "priceStableShort") => string;
}) {
  if (input.movement !== "down" && input.movement !== "up") return null;
  const meta = dealMovementMeta(input.movement, input.t);
  const percent = formatPriceDeltaPercent(input.deltaPercent ?? null, input.locale);
  if (!meta || !percent) return null;
  return { kind: input.movement, meta, percent, source: input.source };
}

function hasRoundedZeroPriceDelta(value: string | null | undefined) {
  if (value == null) return false;
  const ratio = Number(value);
  return Number.isFinite(ratio) && Math.round(Math.abs(ratio) * 100) === 0;
}

export function offerMovementSignal(
  offer: {
    dealDeltaPercent?: string | null;
    dealMovement?: string | null;
    lastChangeDeltaPercent?: string | null;
    lastChangeMovement?: string | null;
  },
  locale: string,
  t: (key: "priceDown" | "priceUp" | "priceStable" | "priceStableShort") => string,
  options: { allowLastChangeFallback?: boolean } = {},
) {
  const dealSignal = directionalMovementSignal({
    deltaPercent: offer.dealDeltaPercent,
    locale,
    movement: offer.dealMovement,
    source: "deal",
    t,
  });
  if (dealSignal) return dealSignal;

  const lastChangeSignal = directionalMovementSignal({
    deltaPercent: offer.lastChangeDeltaPercent,
    locale,
    movement: offer.lastChangeMovement,
    source: "lastChange",
    t,
  });
  if (
    options.allowLastChangeFallback !== false &&
    offer.dealMovement === "unknown" &&
    lastChangeSignal
  ) {
    return lastChangeSignal;
  }

  const flatMovement = dealMovementMeta("flat", t);
  const hasRoundedZeroDelta = [offer.dealDeltaPercent, offer.lastChangeDeltaPercent].some(
    hasRoundedZeroPriceDelta,
  );
  const shouldShowFlat = offer.dealMovement === "flat" || hasRoundedZeroDelta;

  if (flatMovement && shouldShowFlat) {
    return {
      kind: "flat" as const,
      meta: flatMovement,
      percent: t("priceStableShort"),
      source: "deal" as const,
    };
  }

  return null;
}
